import dayjs from 'dayjs';
import type { EChartsOption } from 'echarts';
import { Ndvi } from '../models/ndvi';

/**
 * The NDVI trend chart shared by the maps field panel and Field Analysis, so both offer the same
 * periods, the same Min / Avg / Max and the same axis.
 */

/** Quick periods in months. "All" is 0. */
export const NDVI_QUICK_RANGES = [1, 3, 6];

/** How far back "All" asks for: wide enough for every capture on the platform. */
export const NDVI_ALL_YEARS = 10;

/** Axis labels carry the year (Heinrich, 28/10/2026): "15 Apr 26". The tooltip spells it out. */
export const NDVI_AXIS_FORMAT = 'DD MMM YY';
export const NDVI_TOOLTIP_FORMAT = 'DD MMM YYYY';

export interface NdviStats {
  min: number;
  max: number;
  avg: number;
}

export interface NdviColours {
  min: string;
  max: string;
  mean: string;
}

/** The request window for a quick period ending today; 0 months is "All". */
export function ndviRange(months: number, today = dayjs()): { from: string; to: string } {
  const from = months === 0 ? today.subtract(NDVI_ALL_YEARS, 'year') : today.subtract(months, 'month');
  return { from: from.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
}

/**
 * Lowest minimum, highest maximum and the average of the means over the period. A capture with no
 * value is left out rather than counted as 0, which would pull the average down.
 */
export function ndviStats(data: Ndvi[]): NdviStats | null {
  const present = (values: (number | null | undefined)[]) => values.filter((v): v is number => v != null);
  const mins = present(data.map(d => d.min));
  const maxes = present(data.map(d => d.max));
  const means = present(data.map(d => d.mean));
  if (!mins.length || !maxes.length || !means.length) {
    return null;
  }
  return {
    min: Math.min(...mins),
    max: Math.max(...maxes),
    avg: means.reduce((sum, v) => sum + v, 0) / means.length
  };
}

/**
 * The chart: Min, Max and Mean per capture on a 0 to 1 scale, dated labels on the bottom axis, and a
 * timeline slider when the period is "All". compact is the maps side panel's small type.
 */
export function ndviChartOptions(data: Ndvi[], opts: { slider: boolean; compact: boolean; colours: NdviColours }): EChartsOption {
  const labels = data.map(d => dayjs(d.lastModified).format(NDVI_AXIS_FORMAT));
  const fullDates = data.map(d => dayjs(d.lastModified).format(NDVI_TOOLTIP_FORMAT));
  const font = opts.compact ? 8.5 : 11;
  const round = (v: number | null | undefined) => v == null ? null : +v.toFixed(2);
  const line = (name: string, values: (number | null)[], colour: string, width: number) => ({
    name, type: 'line' as const, data: values, showSymbol: false,
    itemStyle: { color: colour }, lineStyle: { width, color: colour }
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: (params: any) => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';
        const lines = rows.map(p => `${p.marker}${p.seriesName}: ${p.value != null ? Number(p.value).toFixed(2) : 'N/A'}`);
        return [`<strong>${fullDates[rows[0].dataIndex] ?? ''}</strong>`, ...lines].join('<br/>');
      }
    },
    ...(opts.slider ? { dataZoom: [{ type: 'slider' as const, height: 16, bottom: 4, start: 0, end: 100 }] } : {}),
    grid: {
      left: opts.compact ? 6 : 12,
      right: opts.compact ? 10 : 16,
      bottom: opts.slider ? 34 : (opts.compact ? 4 : 8),
      top: opts.compact ? 10 : 16,
      containLabel: true
    },
    xAxis: { type: 'category', boundaryGap: false, data: labels, axisLabel: { fontSize: font, hideOverlap: true } },
    yAxis: { type: 'value', min: 0, max: 1, axisLabel: { fontSize: font }, splitNumber: opts.compact ? 3 : 5 },
    series: [
      line('Min', data.map(d => round(d.min)), opts.colours.min, 1.5),
      line('Max', data.map(d => round(d.max)), opts.colours.max, 1.5),
      line('Mean', data.map(d => round(d.mean)), opts.colours.mean, 2)
    ]
  };
}
